import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as mentorApi from '../../api/mentorApi';
import { useMentorQuery } from '../../api/mentorQuery';
import { formatStudentDisplayName } from '../../utils/year2Ui';
import {
  councilNominationsComplete,
  councilNumberFromSession,
  isCouncilNominationEditable,
  isCouncilSession,
  isOrientationSession,
  sessionStatusKey,
  studentLabel,
} from '../../utils/year2Nominations';

const COUNCIL_LOAD_DEFER_MS = 1200;

function mapCohortRows(rows = []) {
  return rows.map((row) => ({
    id: row.cohort_id ?? row.id,
    name: row.cohort_name ?? row.name,
    schoolId: row.school_id ?? row.schoolId,
    setupStatus: (row.setup_status ?? row.setupStatus) || 'not_started',
  }));
}

function emptyFormRows(squads = []) {
  return squads.map((squad) => ({
    squad_id: squad.squad_id,
    chair1: squad.chair_student_id ? String(squad.chair_student_id) : '',
    chair2: squad.deputy_student_id ? String(squad.deputy_student_id) : '',
    showChair2: Boolean(squad.deputy_student_id),
  }));
}

export default function Year2NominationsSPOC({
  sessions,
  preloadedCohorts = null,
  bootstrapLoading = false,
  bootstrapError = '',
  onBootstrapRetry,
  nominationsCohortId,
  setNominationsCohortId,
  setActiveTab,
  onError,
  onSuccess,
  onTasksRefresh,
}) {
  const usePreloadedCohorts = Array.isArray(preloadedCohorts);

  const { data: queriedCohortRows, isLoading: queryCohortsLoading, error: queryCohortsError, refetch: refetchCohorts } = useMentorQuery(
    ['year2', 'setup', 'cohorts-for-user'],
    () => mentorApi.getYear2SquadSetupCohortsForUser(),
    { enabled: !usePreloadedCohorts && !bootstrapLoading && !bootstrapError, caller: 'Year2NominationsSPOC:cohorts' }
  );

  const [apiCohorts, setApiCohorts] = useState(() => (
    usePreloadedCohorts ? mapCohortRows(preloadedCohorts) : []
  ));
  const cohortsLoading = !usePreloadedCohorts && queryCohortsLoading;
  const cohortsError = !usePreloadedCohorts ? (queryCohortsError?.message || null) : null;
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [formRows, setFormRows] = useState([]);
  const [nominationCache, setNominationCache] = useState({});
  const [saving, setSaving] = useState(false);
  const [loadingCouncil, setLoadingCouncil] = useState(null);
  const councilLoadStartedRef = useRef('');

  const myCohorts = useMemo(
    () => [...apiCohorts].sort((a, b) => String(a.name).localeCompare(String(b.name))),
    [apiCohorts]
  );

  const activeCohortObj = myCohorts.find((c) => String(c.id) === String(nominationsCohortId));
  // Setup status comes from the discovery endpoint (schools/cohorts), which auditors can
  // read. The full squad-setup "show" endpoint stays PA/SPOC-only, so it must not be called
  // here or auditors get "Forbidden: insufficient role" just opening this screen.
  const setupStatus = activeCohortObj?.setupStatus ?? null;
  const setupLoading = cohortsLoading;
  const setupError = cohortsError;

  useEffect(() => {
    if (usePreloadedCohorts) {
      setApiCohorts(mapCohortRows(preloadedCohorts));
    } else if (queriedCohortRows) {
      setApiCohorts(mapCohortRows(queriedCohortRows));
    }
  }, [usePreloadedCohorts, preloadedCohorts, queriedCohortRows]);

  useEffect(() => {
    if (!nominationsCohortId && myCohorts.length > 0) {
      setNominationsCohortId(myCohorts[0].id);
    }
  }, [nominationsCohortId, myCohorts, setNominationsCohortId]);

  const loadCouncilNominations = useCallback(async (cohortId, councilNumber) => {
    const key = `${cohortId}:${councilNumber}`;
    setLoadingCouncil(councilNumber);
    try {
      const data = await mentorApi.getYear2CouncilNominations(cohortId, councilNumber);
      setNominationCache((prev) => ({ ...prev, [key]: data }));
      return data;
    } catch (err) {
      onError?.(err?.message || 'Failed to load council nominations');
      return null;
    } finally {
      setLoadingCouncil(null);
    }
  }, [onError]);

  const cohortSessions = useMemo(() => sessions
    .filter((s) => {
      if (String(s.cohortId) === String(nominationsCohortId)) return true;
      if (s.groupId && (s.groupCohortIds || []).map(String).includes(String(nominationsCohortId))) return true;
      return false;
    })
    .sort((a, b) => String(a.scheduledAt).localeCompare(String(b.scheduledAt))),
  [sessions, nominationsCohortId]);

  const scheduledCouncilNumbers = useMemo(() => (
    [...new Set(
      cohortSessions
        .filter((sess) => isCouncilSession(sess))
        .map((sess) => councilNumberFromSession(sess))
        .filter((n) => n != null)
    )].sort((a, b) => a - b)
  ), [cohortSessions]);

  useEffect(() => {
    if (setupStatus !== 'finalized' || !nominationsCohortId || !scheduledCouncilNumbers.length) return;

    const loadKey = `${nominationsCohortId}:${scheduledCouncilNumbers.join(',')}`;
    if (councilLoadStartedRef.current === loadKey) return;
    councilLoadStartedRef.current = loadKey;

    let cancelled = false;
    const timer = setTimeout(async () => {
      const results = [];
      for (const councilNumber of scheduledCouncilNumbers) {
        if (cancelled) return;
        const row = await loadCouncilNominations(nominationsCohortId, councilNumber);
        if (row) results.push(row);
      }
      if (!cancelled && results.length) {
        onTasksRefresh?.(nominationsCohortId, results);
      }
    }, COUNCIL_LOAD_DEFER_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    setupStatus,
    nominationsCohortId,
    scheduledCouncilNumbers,
    loadCouncilNominations,
    onTasksRefresh,
  ]);

  const handleCohortChange = (val) => {
    setNominationsCohortId(val);
    setEditingSessionId(null);
    setFormRows([]);
    setNominationCache({});
    councilLoadStartedRef.current = '';
  };

  const councilDataForSession = (session) => {
    const councilNumber = councilNumberFromSession(session);
    if (!councilNumber || !nominationsCohortId) return null;
    return nominationCache[`${nominationsCohortId}:${councilNumber}`] || null;
  };

  const hasNominationsForSession = (session) => {
    const data = councilDataForSession(session);
    return councilNominationsComplete(data?.squads || []);
  };

  const handleStartEdit = async (session) => {
    const councilNumber = councilNumberFromSession(session);
    if (!councilNumber) return;
    let data = councilDataForSession(session);
    if (!data) {
      data = await loadCouncilNominations(nominationsCohortId, councilNumber);
    }
    if (!data) return;
    setFormRows(emptyFormRows(data.squads));
    setEditingSessionId(session.id);
  };

  const handleCancelEdit = () => {
    setEditingSessionId(null);
    setFormRows([]);
  };

  const updateFormRow = (squadId, patch) => {
    setFormRows((prev) => prev.map((row) => (
      String(row.squad_id) === String(squadId) ? { ...row, ...patch } : row
    )));
  };

  const handleSubmit = async (e, session) => {
    e.preventDefault();
    const councilNumber = councilNumberFromSession(session);
    if (!councilNumber || !nominationsCohortId) return;

    const invalid = formRows.some((row) => !row.chair1);
    if (invalid) {
      onError?.('Chair 1 is required for every squad.');
      return;
    }

    setSaving(true);
    try {
      const data = await mentorApi.saveYear2CouncilNominations(
        nominationsCohortId,
        councilNumber,
        formRows.map((row) => ({
          squad_id: row.squad_id,
          chair_student_id: row.chair1,
          deputy_student_id: row.showChair2 && row.chair2 ? row.chair2 : null,
        }))
      );
      setNominationCache((prev) => ({
        ...prev,
        [`${nominationsCohortId}:${councilNumber}`]: data,
      }));
      setEditingSessionId(null);
      setFormRows([]);
      // Cross-council chair reuse is advisory only — it never blocks the
      // save, it just surfaces after the fact so a SPOC can double-check.
      const warnings = Array.isArray(data?.warnings) ? data.warnings : [];
      if (warnings.length > 0) {
        onSuccess?.(`Year 2 role nominations saved. Note: ${warnings.join(' ')}`);
      } else {
        onSuccess?.('Year 2 role nominations saved.');
      }
      onTasksRefresh?.(nominationsCohortId, [data]);
    } catch (err) {
      onError?.(err?.message || 'Failed to save council nominations');
    } finally {
      setSaving(false);
    }
  };

  const renderSquadSummary = (squads = []) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {squads.map((squad) => (
        <div
          key={squad.squad_id}
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(120px, 1.2fr) minmax(100px, 1fr) minmax(100px, 1fr) minmax(100px, 1fr)',
            gap: '8px',
            fontSize: '13px',
            alignItems: 'center',
          }}
        >
          <div>
            <div style={{ fontWeight: 700, color: 'var(--neutral-900)' }}>{squad.name}</div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{squad.topic}</div>
          </div>
          <div>
            <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Chair 1</div>
            <div>{studentLabel(squad.students, squad.chair_student_id)}</div>
          </div>
          <div>
            <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Chair 2</div>
            <div>{studentLabel(squad.students, squad.deputy_student_id)}</div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderNominationForm = (session, councilData) => {
    const councilNumber = councilNumberFromSession(session);
    const squads = councilData?.squads || [];

    return (
      <form onSubmit={(e) => handleSubmit(e, session)}>
        {squads.map((squad) => {
          const row = formRows.find((item) => String(item.squad_id) === String(squad.squad_id)) || {
            squad_id: squad.squad_id,
            chair1: '',
            chair2: '',
            showChair2: false,
          };
          const members = squad.students || [];
          const memberNames = members.map((s) => formatStudentDisplayName(s.name)).join(', ');
          const takenByOther = (squadId) => {
            const other = formRows.filter((item) => String(item.squad_id) !== String(squadId));
            return other.flatMap((item) => [item.chair1, item.showChair2 ? item.chair2 : null]).filter(Boolean);
          };

          return (
            <div key={squad.squad_id} className="form-section-container" style={{ marginBottom: '20px' }}>
              <h3 className="form-section-title">{squad.name} — {squad.topic}</h3>
              <hr className="form-section-divider" />
              {memberNames && (
                <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 12px' }}>
                  Members: {memberNames}
                </p>
              )}
              <div className="form-field-group">
                <label htmlFor={`y2-chair1-${session.id}-${squad.squad_id}`}>Chair 1 *</label>
                <select
                  id={`y2-chair1-${session.id}-${squad.squad_id}`}
                  className="form-control-select"
                  value={row.chair1}
                  onChange={(e) => updateFormRow(squad.squad_id, { chair1: e.target.value })}
                  required
                >
                  <option value="">-- Nominate Chair 1 --</option>
                  {members
                    .filter((stud) => {
                      const blocked = takenByOther(squad.squad_id);
                      return !blocked.includes(String(stud.id)) || String(stud.id) === String(row.chair1);
                    })
                    .filter((stud) => String(stud.id) !== String(row.chair2))
                    .map((stud) => (
                      <option key={stud.id} value={stud.id}>{formatStudentDisplayName(stud.name)}</option>
                    ))}
                </select>
              </div>
              {!row.showChair2 && (
                <button
                  type="button"
                  className="btn-form-toggle-outlined"
                  onClick={() => updateFormRow(squad.squad_id, { showChair2: true })}
                >
                  + Add Chair 2 (Optional)
                </button>
              )}
              {row.showChair2 && (
                <div className="form-field-group animate-fade-in">
                  <label htmlFor={`y2-chair2-${session.id}-${squad.squad_id}`}>Chair 2 (Optional)</label>
                  <select
                    id={`y2-chair2-${session.id}-${squad.squad_id}`}
                    className="form-control-select"
                    value={row.chair2}
                    onChange={(e) => updateFormRow(squad.squad_id, { chair2: e.target.value })}
                  >
                    <option value="">-- Chair 2 --</option>
                    {members
                      .filter((stud) => String(stud.id) !== String(row.chair1))
                      .map((stud) => (
                        <option key={stud.id} value={stud.id}>{formatStudentDisplayName(stud.name)}</option>
                      ))}
                  </select>
                  <button
                    type="button"
                    className="btn-form-toggle-outlined remove-btn"
                    onClick={() => updateFormRow(squad.squad_id, { showChair2: false, chair2: '' })}
                  >
                    Remove Chair 2
                  </button>
                </div>
              )}
            </div>
          );
        })}

        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button type="submit" className="btn-submit-nominations" disabled={saving || loadingCouncil === councilNumber}>
            {hasNominationsForSession(session) ? 'Update Year 2 Role Nominations' : 'Submit Year 2 Role Nominations'}
          </button>
          <button type="button" className="btn-form-toggle-outlined" onClick={handleCancelEdit} disabled={saving}>
            Cancel
          </button>
        </div>
      </form>
    );
  };

  if (bootstrapLoading && !Array.isArray(preloadedCohorts)) {
    return (
      <div className="placeholder-card animate-fade-in">
        <p>Loading Year 2 discovery…</p>
      </div>
    );
  }

  if (bootstrapError && !Array.isArray(preloadedCohorts)) {
    return (
      <div className="placeholder-card animate-fade-in">
        <h2>Could not load Year 2 discovery</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '13px', lineHeight: 1.5 }}>{bootstrapError}</p>
        <button
          type="button"
          className="btn-form-toggle-outlined"
          style={{ marginTop: '12px' }}
          onClick={() => { void (onBootstrapRetry ? onBootstrapRetry().catch(() => {}) : undefined); }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (cohortsLoading) {
    return (
      <div className="placeholder-card animate-fade-in">
        <p>Loading your Year 2 cohorts…</p>
      </div>
    );
  }

  if (cohortsError) {
    return (
      <div className="placeholder-card animate-fade-in">
        <h2>Could not load Year 2 cohorts</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '13px', lineHeight: 1.5 }}>{cohortsError}</p>
        <button
          type="button"
          className="btn-form-toggle-outlined"
          style={{ marginTop: '12px' }}
          onClick={() => { void refetchCohorts(); }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (myCohorts.length === 0) {
    return (
      <div className="placeholder-card animate-fade-in">
        <h2>No Year 2 cohorts</h2>
        <p>You have no Year 2 cohorts assigned.</p>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="dashboard-title-row">
        <div>
          <h1 className="dashboard-title">Year 2 Role Nominations</h1>
          <p className="dashboard-subtitle">
            Nominate squad chairs for each council session. Nominations stay editable while the session is planned or live. The PA manages scheduling.
          </p>
        </div>
      </div>

      <div style={{ marginBottom: '24px', padding: '14px 18px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '13px', color: 'var(--neutral-700)', lineHeight: 1.55 }}>
        <strong>Before the council session:</strong> nominate Chair 1 (and optional Chair 2) for each squad.<br />
        <strong>Prerequisite:</strong> finalize squad setup in Year 2 Squad Creator first.
      </div>

      <div style={{ marginBottom: '28px', maxWidth: '340px' }}>
        <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Cohort</label>
        <select
          className="form-control-select"
          value={nominationsCohortId}
          onChange={(e) => handleCohortChange(e.target.value)}
        >
          {myCohorts.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {setupLoading && (
        <div className="placeholder-card"><p>Checking squad setup…</p></div>
      )}

      {!setupLoading && setupError && (
        <div className="placeholder-card">
          <h2>Could not verify squad setup</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '13px', lineHeight: 1.5 }}>{setupError}</p>
          <button
            type="button"
            className="btn-form-toggle-outlined"
            style={{ marginTop: '12px' }}
            onClick={() => { void (usePreloadedCohorts ? onBootstrapRetry?.() : refetchCohorts?.()); }}
          >
            Retry
          </button>
        </div>
      )}

      {!setupLoading && !setupError && setupStatus !== 'finalized' && (
        <div className="placeholder-card">
          <h2>Squad setup required</h2>
          <p>
            {setupStatus === 'draft'
              ? 'Squads are saved as draft. Finalize them in Year 2 Squad Creator before nominating chairs.'
              : 'Finalize squads in Year 2 Squad Creator before nominating chairs.'}
          </p>
          <button
            type="button"
            className="btn-form-toggle-outlined"
            style={{ marginTop: '12px' }}
            onClick={() => {
              setActiveTab?.('year2');
              window.location.hash = '#/year2';
            }}
          >
            Open Year 2 Squad Creator
          </button>
        </div>
      )}

      {!setupLoading && !setupError && setupStatus === 'finalized' && (
        cohortSessions.length === 0 ? (
          <div style={{ color: '#dc2626', fontWeight: 600, fontSize: '13px', padding: '20px 0' }}>
            No sessions scheduled for this cohort yet. Ask the PA to add sessions first.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {cohortSessions.map((sess) => {
              const statusKey = sessionStatusKey(sess.status);
              const isCompleted = statusKey === 'completed';
              const isCancelled = statusKey === 'cancelled';
              const isLive = statusKey === 'live';
              const isOrientation = isOrientationSession(sess);
              const isCouncil = isCouncilSession(sess);
              const isEditable = isCouncil && isCouncilNominationEditable(sess);
              const councilData = councilDataForSession(sess);
              const hasNominations = hasNominationsForSession(sess);
              const isEditing = String(editingSessionId) === String(sess.id);
              const isGroupSession = Boolean(sess.groupId);
              const dateStr = String(sess.scheduledAt || '').split(/[T ]/)[0];

              const borderColor = isCompleted ? '#10b981'
                : isLive ? '#f59e0b'
                : isCancelled ? '#ef4444'
                : isOrientation ? '#94a3b8'
                : 'var(--primary-navy)';

              const headerBg = isCompleted ? '#f0fdf4'
                : isLive ? '#fffbeb'
                : isCancelled ? '#f9fafb'
                : isOrientation ? '#f8fafc'
                : '#eff6ff';

              const statusIcon = isCompleted ? '✅' : isLive ? '🔴' : isCancelled ? '❌' : isOrientation ? '📘' : '📝';

              return (
                <div key={sess.id} style={{ border: `1px solid ${borderColor}`, borderLeft: `4px solid ${borderColor}`, borderRadius: '10px', overflow: 'hidden' }}>
                  <div style={{ background: headerBg, padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '18px' }}>{statusIcon}</span>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--neutral-900)' }}>{sess.sessionType}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                          {dateStr} • {sess.timeSlot}
                          {isGroupSession && activeCohortObj ? ` • ${activeCohortObj.name}` : ''}
                          {isGroupSession && sess.groupName ? ` (Group: ${sess.groupName})` : ''}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {isOrientation && (
                        <span style={{ fontSize: '11px', background: '#e2e8f0', color: '#475569', padding: '3px 10px', borderRadius: '999px', fontWeight: 600 }}>No nominations</span>
                      )}
                      {isEditable && isEditing && (
                        <span style={{ fontSize: '11px', background: '#dbeafe', color: '#1d4ed8', padding: '3px 10px', borderRadius: '999px', fontWeight: 700 }}>Editing</span>
                      )}
                      {isEditable && hasNominations && !isEditing && (
                        <span style={{ fontSize: '11px', background: '#dcfce7', color: '#15803d', padding: '3px 10px', borderRadius: '999px', fontWeight: 700 }}>Nominated</span>
                      )}
                      {isEditable && !hasNominations && !isEditing && (
                        <span style={{ fontSize: '11px', background: '#dbeafe', color: '#1d4ed8', padding: '3px 10px', borderRadius: '999px', fontWeight: 700 }}>Nominate</span>
                      )}
                      {isLive && (
                        <span style={{ fontSize: '11px', background: '#fef3c7', color: '#b45309', padding: '3px 10px', borderRadius: '999px', fontWeight: 600 }}>Live</span>
                      )}
                    </div>
                  </div>

                  <div style={{ padding: '18px' }}>
                    {isOrientation && (
                      <p style={{ color: 'var(--text-muted)', fontSize: '13px', margin: 0 }}>
                        Orientation — no chair nominations for Year 2.
                      </p>
                    )}

                    {isCouncil && (isCompleted || (isEditable && hasNominations && !isEditing)) && (
                      <div style={{ marginBottom: isEditable ? '14px' : 0 }}>
                        {hasNominations && councilData ? (
                          <div style={{ background: isEditable ? '#ecfdf5' : '#f9fafb', border: `1px solid ${isEditable ? '#6ee7b7' : '#e5e7eb'}`, borderRadius: '10px', padding: '16px 18px' }}>
                            <div style={{ fontSize: '14px', color: 'var(--neutral-800)', fontWeight: 700, marginBottom: '12px' }}>
                              Year 2 Role Nominations
                            </div>
                            {renderSquadSummary(councilData.squads)}
                            {isEditable && (
                              <button type="button" className="btn-form-toggle-outlined" style={{ marginTop: '14px' }} onClick={() => handleStartEdit(sess)}>
                                Edit Year 2 Role Nominations
                              </button>
                            )}
                          </div>
                        ) : isCompleted ? (
                          <p style={{ color: 'var(--text-muted)', fontSize: '13px', margin: 0 }}>No nominations recorded for this session.</p>
                        ) : null}
                      </div>
                    )}

                    {isEditable && isEditing && formRows.length > 0 && (
                      <div>{renderNominationForm(sess, councilData || { squads: formRows.map((row) => ({ squad_id: row.squad_id, students: [] })) })}</div>
                    )}

                    {isEditable && !hasNominations && !isEditing && (
                      <button type="button" className="btn-form-toggle-outlined" onClick={() => handleStartEdit(sess)}>
                        Nominate Year 2 Roles for This Session
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}
